///
/// EarthBrowser - Dataset Browser Window
///
/// Draggable, resizable window component for dataset browsers
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import {
	bringDatasetWindowToFront,
	closeDatasetWindow,
	dockWindowToDrawer,
	toggleMaximizeDatasetWindow,
	updateDatasetWindow,
	type DatasetBrowserWindowState,
} from "@/redux/dialogSlice"
import { useAppDispatch } from "@/redux/store"
import { cn } from "@/utils/utils"
import { ArrowDownToLine, Maximize2, Minimize2, X } from "lucide-react"
import { memo, useCallback, useEffect, useMemo, useState, useRef, type ReactNode } from "react"
import { useIsMobile } from "@/components/utils/use-mobile"

// Default dimensions
const MIN_WIDTH = 400
const MIN_HEIGHT = 300
const MIN_INSET = 5

// Titlebar height for calculations
const TITLEBAR_HEIGHT = 40

// Bottom margin to reserve space for the drawer tab bar and other UI elements
const BOTTOM_MARGIN = 20

export interface DatasetBrowserWindowProps {
	// Window state from Redux
	windowState: DatasetBrowserWindowState

	// Content
	children: ReactNode

	// Icon and color (not stored in Redux to keep state serializable)
	icon?: ReactNode
	iconColor?: string

	// Optional custom actions in titlebar
	headerActions?: ReactNode
	titlebarPrefix?: ReactNode
	titleSuffix?: ReactNode

	// Styling
	className?: string

	// Callback when window is brought to front
	onBringToFront?: () => void
}

const WindowContent = memo(function WindowContent({ children }: { children: ReactNode }) {
	return (
		<div className="flex-1 overflow-hidden rounded-b-2xl">
			{children}
		</div>
	)
})

export function DatasetBrowserWindow({
	windowState,
	children,
	icon,
	iconColor = "text-cyan-400",
	headerActions,
	titlebarPrefix,
	titleSuffix,
	className,
	onBringToFront,
}: DatasetBrowserWindowProps) {
	const dispatch = useAppDispatch()
	const isMobile = useIsMobile()
	const prevZIndexRef = useRef(windowState.zIndex)

	// Local rect state for smooth drag/resize without spamming Redux updates
	const [localRect, setLocalRect] = useState(() => ({
		x: windowState.x,
		y: windowState.y,
		width: windowState.width,
		height: windowState.height,
	}))

	// Coalesce local rect updates to one per animation frame
	const pendingRectRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null)
	const rafIdRef = useRef<number | null>(null)

	const scheduleLocalRectUpdate = useCallback((nextRect: { x: number; y: number; width: number; height: number }) => {
		pendingRectRef.current = nextRect
		if (rafIdRef.current !== null) return
		rafIdRef.current = requestAnimationFrame(() => {
			rafIdRef.current = null
			const pending = pendingRectRef.current
			if (!pending) return
			pendingRectRef.current = null
			setLocalRect(pending)
		})
	}, [])

	// Dragging state
	const [isDragging, setIsDragging] = useState(false)
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

	// Resizing state
	const [isResizing, setIsResizing] = useState(false)
	const [resizeEdge, setResizeEdge] = useState<string | null>(null)
	const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 })
	const [resizeMouseStart, setResizeMouseStart] = useState({ x: 0, y: 0 })

	// Check if this window can be docked
	const canDock = windowState.dockMode !== "window-only"

	// Memoize children reference so WindowContent can bail out during drag/resize
	const memoizedChildren = useMemo(() => children, [children])

	// Keep local rect synced when not actively interacting (so external updates still apply)
	useEffect(() => {
		if (isDragging || isResizing) return
		setLocalRect({
			x: windowState.x,
			y: windowState.y,
			width: windowState.width,
			height: windowState.height,
		})
	}, [windowState.x, windowState.y, windowState.width, windowState.height, isDragging, isResizing])

	// Cleanup any pending RAF on unmount
	useEffect(() => {
		return () => {
			if (rafIdRef.current !== null) {
				cancelAnimationFrame(rafIdRef.current)
			}
		}
	}, [])

	// Watch for z-index changes (window brought to front)
	useEffect(() => {
		// Check if z-index increased (window was brought to front)
		if (windowState.zIndex > prevZIndexRef.current && !isMobile) {
			// Call the callback after a small delay to ensure content is rendered
			const timeoutId = setTimeout(() => {
				onBringToFront?.()
			}, 50)

			return () => clearTimeout(timeoutId)
		}

		// Update the ref for next comparison
		prevZIndexRef.current = windowState.zIndex
	}, [windowState.zIndex, isMobile, onBringToFront])

	// Handle window click - bring to front
	const handleWindowClick = useCallback(() => {
		dispatch(bringDatasetWindowToFront(windowState.id))
	}, [dispatch, windowState.id])

	// Handle close
	const handleClose = useCallback(() => {
		dispatch(closeDatasetWindow(windowState.id))
	}, [dispatch, windowState.id])

	// "Minimize" docks the window back into the bottom drawer
	const handleMinimize = useCallback(() => {
		if (canDock) {
			dispatch(dockWindowToDrawer({ windowId: windowState.id, openDrawer: true }))
		}
	}, [dispatch, windowState.id, canDock])

	// Handle Escape key to dock window (only if it's the top-most window)
	useEffect(() => {
		// Only listen if window is open and not docked
		if (windowState.isDockedInDrawer) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				// Check if this window is the top-most by checking if it has the highest zIndex
				// We do this by checking if any other window has a higher zIndex
				const allWindows = document.querySelectorAll('[data-window-id]')
				let isTopMost = true

				allWindows.forEach((win) => {
					const winZIndex = parseInt(window.getComputedStyle(win).zIndex || '0')
					if (winZIndex > windowState.zIndex) {
						isTopMost = false
					}
				})

				if (isTopMost) {
					e.preventDefault()
					e.stopPropagation()
					handleMinimize()
				}
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [windowState.isDockedInDrawer, windowState.zIndex, handleMinimize])

	// Handle maximize/restore
	const handleMaximize = useCallback(() => {
		dispatch(toggleMaximizeDatasetWindow(windowState.id))
	}, [dispatch, windowState.id])

	// Handle titlebar drag start
	const handleTitleBarPointerDown = useCallback((e: React.PointerEvent) => {
		// Only allow dragging from titlebar, not from buttons or other interactive elements
		if ((e.target as HTMLElement).closest('button')) return
		if (windowState.isMaximized) return

		e.preventDefault()
		e.stopPropagation()

		if (e.pointerType === "touch") {
			(e.target as HTMLElement).setPointerCapture(e.pointerId)
		}

		setDragOffset({
			x: e.clientX - localRect.x,
			y: e.clientY - localRect.y,
		})
		// Ensure local rect starts from current Redux rect when beginning a drag
		setLocalRect({
			x: windowState.x,
			y: windowState.y,
			width: windowState.width,
			height: windowState.height,
		})
		setIsDragging(true)

		// Bring to front when starting to drag
		dispatch(bringDatasetWindowToFront(windowState.id))
	}, [localRect.x, localRect.y, windowState.x, windowState.y, windowState.width, windowState.height, windowState.isMaximized, windowState.id, dispatch])

	// Handle resize start
	const handleResizeStart = useCallback((e: React.PointerEvent, edge: string) => {
		e.preventDefault()
		e.stopPropagation()

		if (e.pointerType === "touch") {
			(e.target as HTMLElement).setPointerCapture(e.pointerId)
		}

		setResizeEdge(edge)
		setResizeStart({
			x: localRect.x,
			y: localRect.y,
			width: localRect.width,
			height: localRect.height,
		})
		setResizeMouseStart({
			x: e.clientX,
			y: e.clientY,
		})
		// Ensure local rect starts from current Redux rect when beginning a resize
		setLocalRect({
			x: windowState.x,
			y: windowState.y,
			width: windowState.width,
			height: windowState.height,
		})
		setIsResizing(true)

		// Bring to front when starting to resize
		dispatch(bringDatasetWindowToFront(windowState.id))
	}, [localRect.x, localRect.y, localRect.width, localRect.height, windowState.x, windowState.y, windowState.width, windowState.height, windowState.id, dispatch])

	// Handle pointer move (drag or resize)
	const handlePointerMove = useCallback((e: PointerEvent) => {
		if (isDragging && !windowState.isMaximized) {
			const newX = e.clientX - dragOffset.x
			const newY = e.clientY - dragOffset.y

			const maxX = window.innerWidth - localRect.width
			const maxY = window.innerHeight - BOTTOM_MARGIN - localRect.height

			scheduleLocalRectUpdate({
				x: Math.max(MIN_INSET, Math.min(newX, maxX - MIN_INSET)),
				y: Math.max(MIN_INSET, Math.min(newY, maxY - MIN_INSET)),
				width: localRect.width,
				height: localRect.height,
			})
		} else if (isResizing && resizeEdge) {
			const deltaX = e.clientX - resizeMouseStart.x
			const deltaY = e.clientY - resizeMouseStart.y

			let newX = resizeStart.x
			let newY = resizeStart.y
			let newWidth = resizeStart.width
			let newHeight = resizeStart.height

			// Calculate maximum dimensions based on viewport
			const maxWindowWidth = window.innerWidth - (MIN_INSET * 2)
			const maxWindowHeight = window.innerHeight - BOTTOM_MARGIN - MIN_INSET

			// Handle horizontal resize
			if (resizeEdge.includes('e')) {
				newWidth = Math.max(MIN_WIDTH, Math.min(maxWindowWidth, resizeStart.width + deltaX))
			} else if (resizeEdge.includes('w')) {
				const proposedWidth = resizeStart.width - deltaX
				if (proposedWidth >= MIN_WIDTH) {
					const constrainedWidth = Math.min(maxWindowWidth, proposedWidth)
					newWidth = constrainedWidth
					newX = resizeStart.x + (resizeStart.width - constrainedWidth)
				}
			}

			// Handle vertical resize
			if (resizeEdge.includes('s')) {
				newHeight = Math.max(MIN_HEIGHT, Math.min(maxWindowHeight, resizeStart.height + deltaY))
			} else if (resizeEdge.includes('n')) {
				const proposedHeight = resizeStart.height - deltaY
				if (proposedHeight >= MIN_HEIGHT) {
					const constrainedHeight = Math.min(maxWindowHeight, proposedHeight)
					newHeight = constrainedHeight
					newY = resizeStart.y + (resizeStart.height - constrainedHeight)
				}
			}

			// Constrain to viewport
			const maxX = window.innerWidth - newWidth
			const maxY = window.innerHeight - BOTTOM_MARGIN - newHeight
			newX = Math.max(MIN_INSET, Math.min(newX, maxX - MIN_INSET))
			newY = Math.max(MIN_INSET, Math.min(newY, maxY - MIN_INSET))

			scheduleLocalRectUpdate({
				x: newX,
				y: newY,
				width: newWidth,
				height: newHeight,
			})
		}
	}, [isDragging, isResizing, resizeEdge, dragOffset, resizeStart, resizeMouseStart, windowState.isMaximized, localRect.width, localRect.height, scheduleLocalRectUpdate])

	// Handle pointer up
	const handlePointerUp = useCallback((e?: PointerEvent) => {
		if (e?.pointerType === "touch") {
			(e.target as HTMLElement)?.releasePointerCapture?.(e.pointerId)
		}

		if (isDragging || isResizing) {
			// Persist final position/size to Redux once at end of interaction
			dispatch(updateDatasetWindow({
				id: windowState.id,
				updates: {
					x: localRect.x,
					y: localRect.y,
					width: localRect.width,
					height: localRect.height,
				},
			}))
		}

		setIsDragging(false)
		setIsResizing(false)
		setResizeEdge(null)
	}, [isDragging, isResizing, windowState.id, dispatch, localRect.x, localRect.y, localRect.width, localRect.height])

	// Set up pointer event listeners
	useEffect(() => {
		if (isDragging || isResizing) {
			document.addEventListener("pointermove", handlePointerMove)
			document.addEventListener("pointerup", handlePointerUp)

			return () => {
				document.removeEventListener("pointermove", handlePointerMove)
				document.removeEventListener("pointerup", handlePointerUp)
			}
		}
	}, [isDragging, isResizing, handlePointerMove, handlePointerUp])

	// Handle viewport resize - constrain window size and position
	useEffect(() => {
		const handleResize = () => {
			// Skip if maximized
			if (windowState.isMaximized) return

			// Calculate maximum dimensions
			const maxWindowWidth = window.innerWidth - (MIN_INSET * 2)
			const maxWindowHeight = window.innerHeight - BOTTOM_MARGIN - MIN_INSET

			// Constrain window size if it exceeds viewport
			let newWidth = windowState.width
			let newHeight = windowState.height
			let needsUpdate = false

			if (windowState.width > maxWindowWidth) {
				newWidth = maxWindowWidth
				needsUpdate = true
			}

			if (windowState.height > maxWindowHeight) {
				newHeight = maxWindowHeight
				needsUpdate = true
			}

			// Constrain window position
			const maxX = window.innerWidth - newWidth
			const maxY = window.innerHeight - BOTTOM_MARGIN - newHeight

			let newX = windowState.x
			let newY = windowState.y

			if (windowState.x > maxX - MIN_INSET || windowState.y > maxY - MIN_INSET) {
				newX = Math.max(MIN_INSET, Math.min(windowState.x, maxX - MIN_INSET))
				newY = Math.max(MIN_INSET, Math.min(windowState.y, maxY - MIN_INSET))
				needsUpdate = true
			}

			if (needsUpdate) {
				dispatch(updateDatasetWindow({
					id: windowState.id,
					updates: {
						x: newX,
						y: newY,
						width: newWidth,
						height: newHeight,
					},
				}))
			}
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [windowState, dispatch])

	// Don't render if docked in drawer
	if (windowState.isDockedInDrawer) return null

	// Calculate window position and size
	const windowStyle: React.CSSProperties = windowState.isMaximized
		? {
				left: 0,
				top: 0,
				width: '100vw',
				height: '100vh',
				zIndex: windowState.zIndex,
		  }
		: {
				left: localRect.x,
				top: localRect.y,
				width: localRect.width,
				height: localRect.height,
				zIndex: windowState.zIndex,
		  }

	return (
		<>
			<div
				data-window-id={windowState.id}
				className={cn(
					"fixed bg-black/60 backdrop-blur-xl rounded-2xl border-2 border-slate-400/50 shadow-2xl flex flex-col transition-all duration-300",
					isDragging && "cursor-move shadow-xl",
					isResizing && "select-none",
					className
				)}
				style={{
					...windowStyle,
					// Avoid CSS transitions fighting the drag updates (causes lag + catch-up)
					transition: (isDragging || isResizing) ? "none" : undefined,
					willChange: (isDragging || isResizing) ? "left, top, width, height" : undefined,
				}}
				onClick={handleWindowClick}
			>
			{/* Titlebar */}
			<div
				className={cn(
					"flex items-center gap-2 px-3 py-2 border-b-2 border-slate-400/40 bg-gradient-to-r from-black/60 to-black/40 select-none backdrop-blur-sm rounded-t-2xl",
					!windowState.isMaximized && "cursor-move"
				)}
				onPointerDown={handleTitleBarPointerDown}
				style={{ height: TITLEBAR_HEIGHT }}
			>
				{/* Titlebar prefix (optional) */}
				{titlebarPrefix}

				{/* Icon */}
				{icon && (
					<div className={cn("flex-shrink-0", iconColor)}>
						{icon}
					</div>
				)}

				{/* Title */}
				<div className="flex-1 min-w-0 flex items-center gap-2">
					<h2 className="text-sm font-semibold text-white truncate">
						{windowState.title}
					</h2>
					{titleSuffix}
				</div>

				{/* Custom actions */}
				{headerActions}

		{/* Window controls */}
		<div className="flex items-center gap-0.5 flex-shrink-0">
			{/* Dock back into the drawer */}
			{canDock && (
				<button
					onClick={handleMinimize}
					className="p-1.5 rounded-full hover:bg-white/10 hover:scale-105 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
					aria-label="Dock in drawer"
					title="Dock in drawer"
				>
					<ArrowDownToLine className="w-4 h-4 text-white/60 transition-colors duration-300 hover:text-white" />
				</button>
			)}

			{/* Maximize/Restore */}
			<button
				onClick={handleMaximize}
				className="p-1.5 rounded-full hover:bg-white/10 hover:scale-105 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
				aria-label={windowState.isMaximized ? "Restore" : "Maximize"}
			>
				{windowState.isMaximized ? (
					<Minimize2 className="w-4 h-4 text-white/60 transition-colors duration-300 hover:text-white" />
				) : (
					<Maximize2 className="w-4 h-4 text-white/60 transition-colors duration-300 hover:text-white" />
				)}
			</button>

			{/* Close */}
			<button
				onClick={handleClose}
				className="p-1.5 rounded-full hover:bg-red-500/20 hover:scale-105 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50"
				aria-label="Close"
			>
				<X className="w-4 h-4 text-white/60 transition-colors duration-300 hover:text-red-400" />
			</button>
		</div>
			</div>

			{/* Content area (memoized so drag doesn't re-render heavy children) */}
			<WindowContent>
				{memoizedChildren}
			</WindowContent>

			{/* Resize handles (only show when not maximized) */}
			{!windowState.isMaximized && (
				<>
					{/* Edge handles */}
					<div
						className="absolute top-0 left-0 right-0 h-1 cursor-n-resize"
						onPointerDown={(e) => handleResizeStart(e, 'n')}
					/>
					<div
						className="absolute bottom-0 left-0 right-0 h-1 cursor-s-resize"
						onPointerDown={(e) => handleResizeStart(e, 's')}
					/>
					<div
						className="absolute top-0 bottom-0 left-0 w-1 cursor-w-resize"
						onPointerDown={(e) => handleResizeStart(e, 'w')}
					/>
					<div
						className="absolute top-0 bottom-0 right-0 w-1 cursor-e-resize"
						onPointerDown={(e) => handleResizeStart(e, 'e')}
					/>

					{/* Corner handles */}
					<div
						className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize"
						onPointerDown={(e) => handleResizeStart(e, 'nw')}
					/>
					<div
						className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize"
						onPointerDown={(e) => handleResizeStart(e, 'ne')}
					/>
					<div
						className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize"
						onPointerDown={(e) => handleResizeStart(e, 'sw')}
					/>
				<div
					className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize"
					onPointerDown={(e) => handleResizeStart(e, 'se')}
				/>
			</>
		)}
		</div>
	</>
)
}

